import { LandingPageContent } from '../types';

/**
 * Seasonal page for the December head term. Published well ahead of the season
 * on purpose: rankings for "christmas gifts for kids" are earned in autumn,
 * not in December.
 */
const christmasGiftsForKids: LandingPageContent = {
	path: '/gifts/christmas-gifts-for-kids',
	seoTitle: 'Christmas Gifts for Kids — Personalized Presents Worth Unwrapping',
	metaDescription:
		'Personalized Christmas gifts for kids: custom storybooks starring your child, coloring books and keepsakes. Made to order — see ordering deadlines for December delivery.',
	heading: 'Christmas Gifts for Kids',
	subheading: 'The present they open last and keep longest.',
	heroImage: '/assets/hero/kid-2/classic_scene.png',
	breadcrumbParents: [{ name: 'Gifts for kids', url: '/gifts-for-kids' }],
	intro: `Every Christmas has the same arc: a mountain of wrapping paper, twenty minutes of chaos, and by New Year most of the toys have already found their level at the bottom of the box.

The gift that escapes that fate is usually the one that is *about* the child. A book where they are the illustrated hero — riding a sleigh, meeting the reindeer, saving the day — gets read on Christmas night, and then again in February, and then to the grandparents over a video call.

One honest warning before anything else: personalized gifts are made to order, and December is when every print queue on earth gets long. The section on ordering deadlines below is the most useful thing on this page.`,
	sections: [
		{
			heading: 'Why personalized gifts work at Christmas specifically',
			body: `Christmas gifts arrive in bulk. A child unwrapping their ninth present is running on momentum, not attention — which is exactly why the gift with their own face on the cover cuts through. It is the one present that could not possibly belong to their sibling.

There is also the reading-aloud ritual. Christmas night, pyjamas, one story before bed — a book where the child is the hero slots into that ritual better than anything else you can wrap.

And it photographs well. The moment a child recognises themselves on a book cover is reliably the best picture of the day.`,
		},
		{
			heading: 'Ideas by age',
			body: `**Toddlers (1–2).** Short and bold. A personalized story with big illustrations and their name on every other page. The win at this age is recognition, not plot.

**Ages 3–5.** The sweet spot. A personalized storybook — ideally with a wintery or adventurous theme — plus a personalized coloring book for the long, housebound days between Christmas and New Year.

**Ages 6–8.** Early readers. A book starring themselves is one of the few gifts that competes with a screen. Longer stories and more detailed art styles suit this age.

**Ages 9+.** Storybooks start to feel young; keepsakes don't. A custom t-shirt or mug with their illustrated character, or a poster of them drawn as an adventurer, lands better.`,
		},
		{
			heading: 'Ordering deadlines, honestly',
			body: `A personalized book is not an off-the-shelf item. The realistic timeline is: artwork generation (fast), **your approval of every page** (as fast as you are), printing (days), and shipping (depends where you are).

- **Order by late November** and everything is comfortable.
- **Early December** is usually fine, but you are now sharing the print queue with everyone else who had the same idea.
- **Mid-December** is a gamble that depends on your country and courier.

If you have missed the window: the artwork itself is ready long before the printed book arrives. A picture of the cover, wrapped with a note that the real thing is coming, is a genuinely good Christmas-morning reveal — we have seen it work.`,
		},
		{
			heading: 'For families celebrating in more than one language',
			body: `A personalized book is one of the few Christmas gifts that can carry a family's language with it. Stories can be produced in several languages — including Greek and right-to-left languages such as Arabic, with the page order and layout mirrored properly.

For grandparents abroad, a book in *their* language, starring *their* grandchild, is about as good as long-distance gifting gets. See our pages on [personalized Greek books](/personalized-greek-books-for-kids) and [personalized Arabic books](/personalized-arabic-books-for-kids).`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'Your child as the illustrated hero — the centrepiece gift.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'For the quiet days between Christmas and New Year.',
		},
		{
			label: 'Custom kids t-shirts',
			href: '/custom-kids-t-shirts',
			description: 'Their illustrated character, printed and wearable.',
		},
		{
			label: 'Browse everything',
			href: '/products',
			description: 'Books, prints, mugs, posters and more.',
		},
	],
	faqs: [
		{
			question: 'When should I order a personalized Christmas gift?',
			answer:
				'By late November for a comfortable margin. Personalized items are made to order — artwork, your approval, printing and shipping all take time, and December print queues are long everywhere. Early December is usually still fine; mid-December is a gamble.',
		},
		{
			question: 'What is a good Christmas gift for a child who has too many toys?',
			answer:
				'A personalized storybook. It is not another toy competing for the same shelf — it is a keepsake the child returns to because they are the main character. It also survives January, which most Christmas toys do not.',
		},
		{
			question: 'Can I get a personalized Christmas book in another language?',
			answer:
				'Yes. Stories can be produced in several languages, including Greek and right-to-left languages such as Arabic, with layout and page order adjusted properly. Books in a grandparent’s language are a popular Christmas order.',
		},
		{
			question: 'What if the book does not arrive before Christmas?',
			answer:
				'The artwork is ready long before the printed copy ships. Wrapping a print-out of the cover with a note that the book is on its way makes a genuinely good reveal — the child gets the surprise twice.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Personalized gifts for kids', href: '/personalized-gifts-for-kids' },
		{
			label: 'Christmas gifts for kids who have everything',
			href: '/blog/christmas-gift-ideas-for-kids-who-have-everything',
		},
	],
	cta: { label: 'Browse Christmas gifts', href: '/products' },
};

export default christmasGiftsForKids;
